import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar, CheckCircle, XCircle, Plus, FileText, AlertTriangle, RefreshCw, Info, PartyPopper, Edit, History, Shield } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const s = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800", cancelled: "bg-slate-100 text-slate-800" };
  return <Badge className={s[status] || s.pending}>{status}</Badge>;
}

export default function LeaveManagement() {
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [dash, setDash] = useState(null);
  const [types, setTypes] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [pending, setPending] = useState([]);
  const [balance, setBalance] = useState([]);
  const [open, setOpen] = useState(false);
  const [actOpen, setActOpen] = useState(false);
  const [selReq, setSelReq] = useState(null);
  const [actType, setActType] = useState(null);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ leave_type_id: "", start_date: "", end_date: "", is_half_day: false, half_day_session: "morning", reason: "" });
  
  // Super Admin - Update Balance State
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [adjLogs, setAdjLogs] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [balanceForm, setBalanceForm] = useState({ user_id: "", leave_type_id: "", adjustment_type: "credit", adjustment_amount: "", reason: "" });

  // AL Team Overview state (manager+ tab)
  const [alOverview, setAlOverview] = useState({ rows: [], totals: { entitlement: 0, used: 0, remaining: 0, lop: 0 }, year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [alOverviewLoading, setAlOverviewLoading] = useState(false);
  const [alHistoryOpen, setAlHistoryOpen] = useState(false);
  const [alHistoryUser, setAlHistoryUser] = useState(null);
  const [alHistory, setAlHistory] = useState([]);
  const [alHistoryLoading, setAlHistoryLoading] = useState(false);
  const [alRecalcLoading, setAlRecalcLoading] = useState(false);

  // Bulk fiscal-year recalc
  const [bulkRecalcOpen, setBulkRecalcOpen] = useState(false);
  const [bulkRecalcLoading, setBulkRecalcLoading] = useState(false);
  const [bulkRecalcResult, setBulkRecalcResult] = useState(null);
  
  const { businessId } = useBusiness();

  const fetchPreview = async (s, e) => {
    if (!s) { setPreview(null); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/leave/preview-days`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ start_date: s, end_date: e || s }) });
      if (r.ok) setPreview(await r.json());
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (form.start_date && !form.is_half_day) { const t = setTimeout(() => fetchPreview(form.start_date, form.end_date), 300); return () => clearTimeout(t); }
    setPreview(null);
  }, [form.start_date, form.end_date, form.is_half_day]);

  useEffect(() => { load(); }, [businessId]);

  const load = async () => {
    setLoading(true);
    const businessQuery = businessId ? `?business_id=${businessId}` : "";
    try {
      const [u, t, b, r, d] = await Promise.all([
        fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/leave/types${businessQuery}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/leave/balance${businessQuery}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/leave/requests${businessQuery}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/leave/dashboard${businessQuery}`, { credentials: "include" })
      ]);
      if (u.ok) { 
        const ud = await u.json(); 
        setUser(ud); 
        if (["super_admin","admin","manager"].includes(ud.role)) { 
          const p = await fetch(`${BACKEND_URL}/api/leave/requests/pending${businessQuery}`, { credentials: "include" }); 
          if (p.ok) setPending(await p.json()); 
        }
        // Super Admin: Load staff list for balance updates
        if (ud.role === "super_admin") {
          const staff = await fetch(`${BACKEND_URL}/api/leave/staff-balances${businessQuery}`, { credentials: "include" });
          if (staff.ok) {
            const staffData = await staff.json();
            setStaffList(staffData.staff_balances || []);
          }
        }
      }
      if (t.ok) setTypes(await t.json());
      if (b.ok) setBalance(await b.json());
      if (r.ok) setReqs(await r.json());
      if (d.ok) setDash(await d.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.leave_type_id || !form.start_date || !form.reason) { toast.error("Fill all fields"); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/leave/requests`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ ...form, end_date: form.end_date || form.start_date }) });
      if (r.ok) { toast.success("Submitted"); setOpen(false); setForm({ leave_type_id: "", start_date: "", end_date: "", is_half_day: false, half_day_session: "morning", reason: "" }); load(); }
      else { const err = await r.json(); toast.error(err.detail || "Failed"); }
    } catch (err) { toast.error("Error"); }
  };

  const act = async () => {
    if (!selReq || !actType) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/leave/requests/${selReq.leave_id}/action`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ action: actType, rejection_reason: actType === "reject" ? reason : null }) });
      if (r.ok) {
        toast.success(actType === "approve" ? "Approved" : "Rejected");
        setActOpen(false); setSelReq(null); setActType(null); setReason("");
        load();
        if (mgr) loadAlOverview();
        window.dispatchEvent(new CustomEvent("hrms:data-changed", { detail: { kind: "leave" } }));
      }
      else { const err = await r.json(); toast.error(err.detail || "Failed"); }
    } catch (err) { toast.error("Error"); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel?")) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/leave/requests/${id}/cancel`, { method: "POST", credentials: "include" });
      if (r.ok) {
        toast.success("Cancelled");
        load();
        if (mgr) loadAlOverview();
        window.dispatchEvent(new CustomEvent("hrms:data-changed", { detail: { kind: "leave" } }));
      } else toast.error("Failed");
    } catch (e) { toast.error("Error"); }
  };

  // Super Admin: Update Balance
  const submitBalanceAdjustment = async (e) => {
    e.preventDefault();
    if (!balanceForm.user_id || !balanceForm.leave_type_id || !balanceForm.reason) { toast.error("Fill all required fields"); return; }
    if (balanceForm.adjustment_type !== "reset" && (!balanceForm.adjustment_amount || parseFloat(balanceForm.adjustment_amount) === 0)) { toast.error("Enter adjustment amount"); return; }
    if (balanceForm.reason.trim().length < 3) { toast.error("Reason must be at least 3 characters"); return; }
    
    try {
      const payload = {
        user_id: balanceForm.user_id,
        leave_type_id: balanceForm.leave_type_id,
        adjustment_type: balanceForm.adjustment_type,
        adjustment_amount: parseFloat(balanceForm.adjustment_amount) || 0,
        reason: balanceForm.reason.trim(),
        year: new Date().getFullYear()
      };
      
      const r = await fetch(`${BACKEND_URL}/api/leave/balance/adjust`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(payload) });
      if (r.ok) {
        const result = await r.json();
        toast.success(`Balance updated: ${result.previous_balance} → ${result.new_balance}`);
        setBalanceOpen(false);
        setBalanceForm({ user_id: "", leave_type_id: "", adjustment_type: "credit", adjustment_amount: "", reason: "" });
        load();
        if (mgr) loadAlOverview();
        window.dispatchEvent(new CustomEvent("hrms:data-changed", { detail: { kind: "leave" } }));
      } else {
        const err = await r.json(); 
        toast.error(err.detail || "Failed to update balance"); 
      }
    } catch (err) { toast.error("Error updating balance"); }
  };

  // Load adjustment logs
  const loadAdjustmentLogs = async (userId = null) => {
    try {
      const url = userId ? `${BACKEND_URL}/api/leave/adjustment-logs?user_id=${userId}` : `${BACKEND_URL}/api/leave/adjustment-logs`;
      const r = await fetch(url, { credentials: "include" });
      if (r.ok) { setAdjLogs(await r.json()); setLogsOpen(true); }
    } catch (e) { console.error(e); }
  };

  // ── AL Team Overview helpers ────────────────────────────────────────
  const loadAlOverview = async () => {
    setAlOverviewLoading(true);
    try {
      const now = new Date();
      const qs = `?business_id=${encodeURIComponent(businessId || "")}&year=${now.getFullYear()}&month=${now.getMonth() + 1}`;
      const r = await fetch(`${BACKEND_URL}/api/leave/al-overview${qs}`, { credentials: "include" });
      if (r.ok) setAlOverview(await r.json());
      else toast.error("Failed to load AL overview");
    } catch (e) { console.error(e); toast.error("Failed to load AL overview"); }
    finally { setAlOverviewLoading(false); }
  };

  const openAlHistory = async (row) => {
    setAlHistoryUser(row);
    setAlHistoryOpen(true);
    setAlHistoryLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/leave/al-history?user_id=${encodeURIComponent(row.user_id)}&year=${alOverview.year}&business_id=${encodeURIComponent(businessId || "")}`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setAlHistory(data.history || []);
      }
    } catch (e) { console.error(e); }
    finally { setAlHistoryLoading(false); }
  };

  const runBulkRecalc = async () => {
    setBulkRecalcLoading(true);
    setBulkRecalcResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/leave/al-full-recalculate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_id: businessId }),
      });
      const data = await r.json();
      if (r.ok) {
        setBulkRecalcResult(data);
        toast.success(`Processed ${data.users_processed} employee(s).`);
        await loadAlOverview();
        await load();
      } else {
        toast.error(data.detail || "Recalculation failed");
        setBulkRecalcOpen(false);
      }
    } catch (e) { console.error(e); toast.error("Recalculation failed"); }
    finally { setBulkRecalcLoading(false); }
  };

  const recalcAl = async (userId = null) => {
    if (!window.confirm(userId ? "Recalculate AL balance for this user?" : "Recalculate AL balances for ALL employees? Drift-only — corrects mismatches without changing real consumption.")) return;
    setAlRecalcLoading(true);
    try {
      const body = { business_id: businessId, year: alOverview.year };
      if (userId) body.user_id = userId;
      const r = await fetch(`${BACKEND_URL}/api/leave/al-recalculate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Checked ${data.users_checked} user(s). ${data.corrections_applied} correction(s) applied.`);
        await load();
        await loadAlOverview();
        window.dispatchEvent(new CustomEvent("hrms:data-changed", { detail: { kind: "leave" } }));
      } else {
        const data = await r.json().catch(() => ({}));
        toast.error(data.detail || "Recalculation failed");
      }
    } catch (e) { console.error(e); toast.error("Recalculation failed"); }
    finally { setAlRecalcLoading(false); }
  };

  useEffect(() => {
    if (mgr && tab === "team-al") loadAlOverview();
    // eslint-disable-line
  }, [tab, businessId]); // eslint-disable-line

  const isSuperAdmin = user?.role === "super_admin";
  const mgr = user?.role && ["super_admin", "admin", "manager"].includes(user.role);

  // Hooks for the redesigned page — MUST be declared before any conditional
  // return below, or React will trip Rules-of-Hooks when `loading` flips.
  const [empSearch, setEmpSearch] = useState("");
  const [empFilter, setEmpFilter] = useState("all"); // all | with-payroll | absorbed | lop
  useEffect(() => { if (mgr) loadAlOverview(); /* eslint-disable-next-line */ }, [user?.user_id, businessId]);

  if (loading) return <div className="text-center py-12">Loading...</div>;

  // ── Derived self-stats for the redesigned summary ────────────────────────
  const myAlRow = (alOverview.rows || []).find(r => r.user_id === user?.user_id) || null;
  const myAlBal = balance.find(b => b.leave_type_code === "AL") || null;
  const selfEntitlement = Number(myAlRow?.annual_leave_entitlement ?? myAlBal?.total_days ?? 12);
  const selfConsumed = Number(myAlRow?.annual_leave_used ?? myAlBal?.used_days ?? 0);
  const selfRemaining = Number(myAlRow?.annual_leave_remaining ?? myAlBal?.available_days ?? Math.max(0, selfEntitlement - selfConsumed));
  const selfAbsorbedThisMonth = Number(myAlRow?.absorbed_this_month ?? 0);
  const selfLopThisMonth = Number(myAlRow?.chargeable_absent_this_month ?? 0);
  const selfLastUpdated = myAlRow?.last_updated || null;

  // Leave request roll-ups (year-to-date) from reqs
  const yearNow = new Date().getFullYear();
  const reqsThisYear = reqs.filter(r => (r.start_date || "").slice(0, 4) === String(yearNow));
  const totalTaken = reqsThisYear.filter(r => r.status === "approved").reduce((s, r) => s + (Number(r.total_days) || 0), 0);
  const totalApproved = totalTaken; // approved leave = leave taken in our model
  const totalPending  = reqsThisYear.filter(r => r.status === "pending").reduce((s, r) => s + (Number(r.total_days) || 0), 0);

  // Employee table — filtered client-side (search + filter state declared
  // above the loading guard to obey Rules of Hooks).
  const filteredEmps = (alOverview.rows || []).filter(r => {
    const q = empSearch.toLowerCase();
    if (q && !(r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q))) return false;
    if (empFilter === "with-payroll" && !r.has_payroll) return false;
    if (empFilter === "absorbed" && !(r.absorbed_this_month > 0)) return false;
    if (empFilter === "lop" && !(r.chargeable_absent_this_month > 0)) return false;
    return true;
  });

  const exportCsv = () => {
    const rows = filteredEmps;
    const headers = ["Employee Name","Email","Entitlement Leave","Consumed Leave","Remaining Leave","LOP This Month","Last Updated"];
    const lines = [headers.join(",")];
    for (const r of rows) {
      const cells = [
        r.name || "",
        r.email || "",
        r.annual_leave_entitlement,
        r.annual_leave_used,
        r.annual_leave_remaining,
        r.has_payroll ? r.chargeable_absent_this_month : "",
        r.last_updated ? r.last_updated.slice(0,10) : ""
      ].map(v => {
        const s = String(v ?? "");
        return s.includes(",") || s.includes('"') ? '"' + s.replace(/"/g,'""') + '"' : s;
      });
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `leave-balances-${yearNow}.csv`; a.click();
    URL.revokeObjectURL(u);
  };

  const refresh = async () => { await load(); if (mgr) await loadAlOverview(); };

  return (
    <div className="space-y-6 bg-slate-50 dark:bg-slate-950 -mx-4 -my-4 sm:-mx-6 sm:-my-6 px-4 py-4 sm:px-6 sm:py-6 min-h-screen">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Leave Management</h1>
          <p className="text-sm text-slate-500">View Leave Balance Summary</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={refresh} variant="outline" size="sm" className="rounded-xl"><RefreshCw className="w-4 h-4 mr-2"/>Refresh</Button>
          {mgr && <Button onClick={exportCsv} variant="outline" size="sm" className="rounded-xl"><FileText className="w-4 h-4 mr-2"/>Export Report</Button>}
          {isSuperAdmin && (() => {
            const t = new Date();
            const cycleY = t.getMonth() + 1 >= 4 ? t.getFullYear() : t.getFullYear() - 1;
            const label = `Recalculate Leave Balance (01-Apr-${cycleY} to Current Date)`;
            return (
              <Button
                onClick={() => { setBulkRecalcResult(null); setBulkRecalcOpen(true); }}
                size="sm"
                className="rounded-xl bg-indigo-600 hover:bg-indigo-700 shadow-sm"
                title={label}
                data-testid="bulk-recalc-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2"/>
                <span className="hidden xl:inline">{label}</span>
                <span className="xl:hidden">Bulk Recalc</span>
              </Button>
            );
          })()}
          {isSuperAdmin && <Button onClick={() => recalcAl(null)} variant="outline" size="sm" className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20" disabled={alRecalcLoading} title="Drift-only verify (no replay)"><RefreshCw className={`w-4 h-4 mr-2 ${alRecalcLoading ? "animate-spin" : ""}`}/>Verify Drift</Button>}
          {isSuperAdmin && <Button onClick={() => loadAdjustmentLogs()} variant="outline" size="sm" className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50"><History className="w-4 h-4 mr-2"/>Audit Logs</Button>}
          {isSuperAdmin && <Button onClick={() => setBalanceOpen(true)} variant="outline" size="sm" className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50"><Edit className="w-4 h-4 mr-2"/>Update Balance</Button>}
          <Button onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm" size="sm"><Plus className="w-4 h-4 mr-2"/>Apply Leave</Button>
        </div>
      </div>

      {/* ── 4 KPI cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Entitlement"
          value={selfEntitlement}
          suffix="Days"
          subtle="Available leaves for the year"
          icon={<Calendar className="w-5 h-5 text-blue-600"/>}
          tint="blue"
        />
        <KpiCard
          label="Consumed Till Date"
          value={selfConsumed}
          suffix="Days"
          subtle="Leave used till today"
          icon={<CheckCircle className="w-5 h-5 text-amber-600"/>}
          tint="amber"
        />
        <KpiCard
          label="Remaining Leave"
          value={selfRemaining}
          suffix="Days"
          subtle="Balance leaves available"
          icon={<PartyPopper className="w-5 h-5 text-emerald-600"/>}
          tint="emerald"
        />
        <KpiCard
          label="LOP This Month"
          value={selfLopThisMonth}
          suffix={selfLopThisMonth === 1 ? "Day" : "Days"}
          subtle="Loss of Pay this month"
          icon={<AlertTriangle className="w-5 h-5 text-red-600"/>}
          tint="red"
        />
      </div>

      {/* ── Leave Summary (left: stats / right: donut) ──────────────────── */}
      <Card className="rounded-2xl shadow-sm border-slate-200 dark:border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Leave Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Stats list — 2 of 3 columns */}
            <div className="md:col-span-2 space-y-3">
              <StatRow label="Total Annual Leave Entitlement"  value={`${selfEntitlement} Days`} tone="default"/>
              <StatRow label="Leave Consumed Till Date"        value={`${selfConsumed} Days`}   tone="amber"/>
              <StatRow label="Remaining Leave Balance"         value={`${selfRemaining} Days`}  tone="emerald" bold/>
              <StatRow label="Leave Deducted from Absent Days" value={`${selfAbsorbedThisMonth} Days`} tone="purple"/>
              <StatRow label="LOP (Loss of Pay) This Month"    value={`${selfLopThisMonth} ${selfLopThisMonth===1?"Day":"Days"}`} tone="red"/>
              <div className="border-t border-slate-100 dark:border-slate-800 my-2"/>
              <StatRow label="Total Leave Taken"     value={`${totalTaken} Days`}    tone="default"/>
              <StatRow label="Total Leave Approved"  value={`${totalApproved} Days`} tone="emerald"/>
              <StatRow label="Total Leave Pending"   value={`${totalPending} Days`}  tone="amber"/>
              {selfLastUpdated && <p className="text-xs text-slate-400 pt-1">Last updated {format(parseISO(selfLastUpdated), "dd MMM yyyy")}</p>}
            </div>

            {/* Donut chart — 1 of 3 columns */}
            <div className="flex flex-col items-center justify-center">
              <LeaveDonut entitlement={selfEntitlement} consumed={selfConsumed} lop={selfLopThisMonth} remaining={selfRemaining}/>
              <div className="mt-4 w-full space-y-1.5 text-sm">
                <LegendRow color="#10b981" label="Remaining Leave" value={`${selfRemaining} Days`}/>
                <LegendRow color="#f59e0b" label="Consumed Leave"  value={`${selfConsumed} Days`}/>
                <LegendRow color="#ef4444" label="LOP"             value={`${selfLopThisMonth} ${selfLopThisMonth===1?"Day":"Days"}`}/>
                <div className="border-t border-slate-100 dark:border-slate-800 pt-2 flex justify-between text-slate-600">
                  <span>Total Entitlement</span>
                  <span className="font-semibold">{selfEntitlement} Days</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Pending Approvals (only for managers, when there's something to do) */}
      {mgr && pending.length > 0 && (
        <Card className="rounded-2xl shadow-sm border-l-4 border-l-amber-400 border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500"/> Pending Approvals
              <Badge className="bg-amber-100 text-amber-800">{pending.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pending.slice(0, 3).map(p => (
                <div key={p.leave_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{p.user_name}</span>
                    <Badge variant="outline">{p.leave_type_code}</Badge>
                    <span className="text-xs text-slate-500">{format(parseISO(p.start_date), "dd MMM")} – {format(parseISO(p.end_date), "dd MMM")} ({p.total_days}d)</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 rounded-lg" onClick={() => { setSelReq(p); setActType("approve"); setActOpen(true); }}><CheckCircle className="w-3.5 h-3.5"/></Button>
                    <Button size="sm" variant="outline" className="h-8 rounded-lg text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setSelReq(p); setActType("reject"); setActOpen(true); }}><XCircle className="w-3.5 h-3.5"/></Button>
                  </div>
                </div>
              ))}
              {pending.length > 3 && <p className="text-xs text-slate-500 pl-1">+{pending.length - 3} more pending…</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Employee Leave Table (manager+) ─────────────────────────────── */}
      {mgr && (
        <Card className="rounded-2xl shadow-sm border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-3">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <CardTitle className="text-base font-semibold">Employee Leave Balances · {new Date(0, alOverview.month - 1).toLocaleString("default", { month: "long" })} {alOverview.year}</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="search"
                  placeholder="Search employee…"
                  value={empSearch}
                  onChange={(e) => setEmpSearch(e.target.value)}
                  className="h-9 rounded-xl w-full sm:w-56"
                  data-testid="emp-search"
                />
                <Select value={empFilter} onValueChange={setEmpFilter}>
                  <SelectTrigger className="h-9 rounded-xl w-full sm:w-44" data-testid="emp-filter"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All employees</SelectItem>
                    <SelectItem value="with-payroll">With payroll set up</SelectItem>
                    <SelectItem value="absorbed">Absorbed AL this month</SelectItem>
                    <SelectItem value="lop">Has LOP this month</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={exportCsv} variant="outline" size="sm" className="rounded-xl"><FileText className="w-4 h-4 mr-2"/>Export CSV</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100 dark:border-slate-800">
                    <TableHead>Employee Name</TableHead>
                    <TableHead className="text-center"><span className="text-blue-700">Entitlement</span></TableHead>
                    <TableHead className="text-center"><span className="text-amber-700">Consumed</span></TableHead>
                    <TableHead className="text-center"><span className="text-emerald-700">Remaining</span></TableHead>
                    <TableHead className="text-center"><span className="text-red-700">LOP this month</span></TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alOverviewLoading && (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
                  )}
                  {!alOverviewLoading && filteredEmps.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">No employees match the filter.</TableCell></TableRow>
                  )}
                  {!alOverviewLoading && filteredEmps.map((r) => (
                    <TableRow key={r.user_id} className={`border-slate-100 dark:border-slate-800 ${!r.has_payroll ? "opacity-60" : ""}`}>
                      <TableCell>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                        {!r.has_payroll && <span className="text-[10px] uppercase tracking-wider text-amber-600">no payroll</span>}
                      </TableCell>
                      <TableCell className="text-center font-medium text-blue-700">{r.annual_leave_entitlement}</TableCell>
                      <TableCell className="text-center font-medium text-amber-700">{r.annual_leave_used}</TableCell>
                      <TableCell className="text-center font-bold text-emerald-700">{r.annual_leave_remaining}</TableCell>
                      <TableCell className={`text-center font-medium ${r.chargeable_absent_this_month > 0 ? "text-red-600" : "text-slate-400"}`}>{r.has_payroll ? r.chargeable_absent_this_month : "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => openAlHistory(r)} title="View leave history"><History className="w-4 h-4"/></Button>
                          {isSuperAdmin && <Button size="sm" variant="ghost" className="h-8 rounded-lg text-purple-700" onClick={() => recalcAl(r.user_id)} disabled={alRecalcLoading} title="Recalculate balance"><RefreshCw className="w-4 h-4"/></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {filteredEmps.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-800">
                <span>Showing {filteredEmps.length} of {alOverview.rows.length} employees</span>
                <span>Balances auto-update after leave approval, attendance processing, and payroll finalization.</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── My Recent Requests (compact, only when there are any) ───────── */}
      {reqs.length > 0 && (
        <Card className="rounded-2xl shadow-sm border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">My Recent Requests</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100 dark:border-slate-800">
                    <TableHead>Type</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead className="text-center">Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reqs.slice(0, 8).map(r => (
                    <TableRow key={r.leave_id} className="border-slate-100 dark:border-slate-800">
                      <TableCell><Badge variant="outline">{r.leave_type_code}</Badge></TableCell>
                      <TableCell>{format(parseISO(r.start_date), "dd MMM yyyy")}</TableCell>
                      <TableCell>{format(parseISO(r.end_date), "dd MMM yyyy")}</TableCell>
                      <TableCell className="text-center">{r.total_days}</TableCell>
                      <TableCell><StatusBadge status={r.status}/></TableCell>
                      <TableCell className="text-right">{(r.status === "pending" || r.status === "approved") && <Button size="sm" variant="ghost" className="text-red-600 h-8" onClick={() => cancel(r.leave_id)}>Cancel</Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Leave Policy notice ─────────────────────────────────────────── */}
      <Card className="rounded-2xl shadow-sm border-slate-200 dark:border-slate-800 bg-emerald-50/40 dark:bg-emerald-900/10">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="mt-0.5 w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0"><Info className="w-4 h-4 text-emerald-700"/></div>
          <div className="text-sm">
            <p className="font-semibold text-slate-800 dark:text-slate-100">Annual Leave Policy</p>
            <p className="text-slate-600 dark:text-slate-300 mt-0.5">You are entitled to <strong>{selfEntitlement} Annual Leaves per year</strong>. Leave balance is updated based on attendance and leave approvals — absent days first absorb your AL, anything past zero becomes Loss of Pay.</p>
          </div>
        </CardContent>
      </Card>


      {/* Apply Leave Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Apply Leave</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2"><Label>Type *</Label><Select value={form.leave_type_id} onValueChange={v => setForm({...form, leave_type_id: v})}><SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger><SelectContent>{types.map(t => <SelectItem key={t.leave_type_id} value={t.leave_type_id}>{t.code} - {t.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label>From *</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}/></div><div className="space-y-2"><Label>To</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} min={form.start_date}/></div></div>
            {preview && !form.is_half_day && <div className="p-3 rounded-lg bg-blue-50 border border-blue-200"><div className="flex items-center gap-2 mb-2"><Info className="w-4 h-4 text-blue-600"/><span className="text-sm font-medium text-blue-700">Leave Days</span></div><div className="text-sm space-y-1"><div className="flex justify-between"><span>Total:</span><span>{preview.raw_days}d</span></div>{preview.holidays_excluded > 0 && <div className="flex justify-between text-amber-600"><span><PartyPopper className="w-3 h-3 inline mr-1"/>Holidays:</span><span>-{preview.holidays_excluded}</span></div>}<div className="flex justify-between font-semibold text-blue-700 pt-1 border-t"><span>Leave:</span><span>{preview.leave_days}d</span></div></div></div>}
            <div className="flex items-center gap-4"><div className="flex items-center gap-2"><input type="checkbox" id="hd" checked={form.is_half_day} onChange={e => setForm({...form, is_half_day: e.target.checked})} className="w-4 h-4"/><Label htmlFor="hd">Half Day</Label></div>{form.is_half_day && <Select value={form.half_day_session} onValueChange={v => setForm({...form, half_day_session: v})}><SelectTrigger className="w-32"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="morning">Morning</SelectItem><SelectItem value="afternoon">Afternoon</SelectItem></SelectContent></Select>}</div>
            <div className="space-y-2"><Label>Reason *</Label><Textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="Enter reason..." rows={2}/></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => { setOpen(false); setPreview(null); }}>Cancel</Button><Button type="submit">Submit</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actOpen} onOpenChange={setActOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>{actType === "approve" ? "Approve" : "Reject"} Leave</DialogTitle></DialogHeader>
          {selReq && <div className="space-y-4"><div className="p-4 rounded-lg bg-slate-50"><p><span className="font-medium">Employee:</span> {selReq.user_name}</p><p><span className="font-medium">Leave:</span> {selReq.leave_type_name}</p><p><span className="font-medium">Period:</span> {format(parseISO(selReq.start_date), "dd MMM")} - {format(parseISO(selReq.end_date), "dd MMM")} ({selReq.total_days}d)</p></div>{actType === "reject" && <div className="space-y-2"><Label>Reason</Label><Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Enter reason..." rows={2}/></div>}</div>}
          <DialogFooter><Button variant="outline" onClick={() => { setActOpen(false); setReason(""); }}>Cancel</Button><Button onClick={act} className={actType === "approve" ? "bg-emerald-600" : "bg-red-600"}>{actType === "approve" ? "Approve" : "Reject"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super Admin: Update Balance Dialog */}
      <Dialog open={balanceOpen} onOpenChange={setBalanceOpen}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-purple-600"/>Update Leave Balance</DialogTitle></DialogHeader>
          <form onSubmit={submitBalanceAdjustment} className="space-y-4">
            <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-sm text-purple-700"><Shield className="w-4 h-4 inline mr-1"/>Super Admin Only - All changes are logged in audit trail</div>
            
            <div className="space-y-2"><Label>Select Employee *</Label>
              <Select value={balanceForm.user_id} onValueChange={v => setBalanceForm({...balanceForm, user_id: v})} data-testid="balance-employee-select">
                <SelectTrigger><SelectValue placeholder="Select employee"/></SelectTrigger>
                <SelectContent>{staffList.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name} ({s.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2"><Label>Leave Type *</Label>
              <Select value={balanceForm.leave_type_id} onValueChange={v => setBalanceForm({...balanceForm, leave_type_id: v})} data-testid="balance-type-select">
                <SelectTrigger><SelectValue placeholder="Select leave type"/></SelectTrigger>
                <SelectContent>{types.map(t => <SelectItem key={t.leave_type_id} value={t.leave_type_id}>{t.code} - {t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2"><Label>Adjustment Type *</Label>
              <Select value={balanceForm.adjustment_type} onValueChange={v => setBalanceForm({...balanceForm, adjustment_type: v})} data-testid="balance-adjustment-type">
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit"><span className="text-emerald-600">+ Increase</span> Leave Count</SelectItem>
                  <SelectItem value="debit"><span className="text-red-600">- Decrease</span> Leave Count</SelectItem>
                  <SelectItem value="reset"><span className="text-blue-600">↻ Reset</span> to Specific Value</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2"><Label>{balanceForm.adjustment_type === "reset" ? "New Balance Value *" : "Adjustment Amount *"}</Label>
              <Input type="number" step="0.5" min="0" value={balanceForm.adjustment_amount} onChange={e => setBalanceForm({...balanceForm, adjustment_amount: e.target.value})} placeholder={balanceForm.adjustment_type === "reset" ? "Enter new balance" : "Enter amount"} data-testid="balance-amount-input"/>
            </div>
            
            <div className="space-y-2"><Label>Reason (Mandatory) *</Label>
              <Textarea value={balanceForm.reason} onChange={e => setBalanceForm({...balanceForm, reason: e.target.value})} placeholder="Enter detailed reason for this adjustment..." rows={3} data-testid="balance-reason-input"/>
              <p className="text-xs text-slate-500">This reason will be recorded in audit log</p>
            </div>
            
            <DialogFooter><Button type="button" variant="outline" onClick={() => setBalanceOpen(false)}>Cancel</Button><Button type="submit" className="bg-purple-600" data-testid="save-balance-btn">Save Changes</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Audit Logs Dialog */}
      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto"><DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-amber-600"/>Leave Balance Adjustment Logs</DialogTitle></DialogHeader>
          {adjLogs.length === 0 ? <div className="text-center py-8 text-slate-500">No adjustment logs found</div> :
          <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Employee</TableHead><TableHead>Type</TableHead><TableHead>Action</TableHead><TableHead>Change</TableHead><TableHead>Reason</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
            <TableBody>{adjLogs.map((log, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs">{format(parseISO(log.created_at), "dd MMM yy HH:mm")}</TableCell>
                <TableCell className="font-medium">{log.user_name}</TableCell>
                <TableCell><Badge variant="outline">{log.leave_type_code}</Badge></TableCell>
                <TableCell><Badge className={log.adjustment_type === "credit" ? "bg-emerald-100 text-emerald-800" : log.adjustment_type === "debit" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}>{log.adjustment_type}</Badge></TableCell>
                <TableCell><span className="text-slate-500">{log.previous_balance}</span> → <span className="font-bold">{log.new_balance}</span></TableCell>
                <TableCell className="max-w-[200px] truncate" title={log.reason}>{log.reason}</TableCell>
                <TableCell className="text-xs text-slate-500">{log.adjusted_by_name}</TableCell>
              </TableRow>
            ))}</TableBody></Table>}
          <DialogFooter><Button variant="outline" onClick={() => setLogsOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──────── BULK FISCAL RECALC DIALOG ──────── */}
      <Dialog open={bulkRecalcOpen} onOpenChange={(o) => { if (!bulkRecalcLoading) setBulkRecalcOpen(o); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-indigo-600"/>
              Bulk Leave Balance Recalculation
            </DialogTitle>
          </DialogHeader>
          {!bulkRecalcResult && !bulkRecalcLoading && (() => {
            const t = new Date();
            const cycleY = t.getMonth() + 1 >= 4 ? t.getFullYear() : t.getFullYear() - 1;
            return (
              <div className="space-y-4 text-sm">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-amber-800 dark:text-amber-200 space-y-2">
                  <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4"/> This will replay the cycle from scratch.</div>
                  <ul className="text-xs space-y-1 ml-6 list-disc">
                    <li>Cycle: <strong>01-Apr-{cycleY}</strong> → <strong>today</strong></li>
                    <li>Resets every employee's AL balance to 12 days</li>
                    <li>Re-absorbs absences month-by-month from attendance records</li>
                    <li>Charges approved AL leave requests within the cycle</li>
                    <li>Anything past 12 days becomes LOP</li>
                    <li>Deletes prior <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">auto_absorption</code> logs for the cycle (not historical leaves)</li>
                    <li>Writes a fresh audit trail with batch ID</li>
                  </ul>
                </div>
                <p className="text-slate-600 dark:text-slate-300">Use this when balances have drifted significantly or after a policy change. For routine drift correction, use "Verify Drift" instead.</p>
              </div>
            );
          })()}
          {bulkRecalcLoading && (
            <div className="py-10 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-sm text-slate-500">Processing all employees…</p>
            </div>
          )}
          {bulkRecalcResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3"><p className="text-2xl font-bold text-emerald-700">{bulkRecalcResult.users_processed}</p><p className="text-[10px] uppercase tracking-wider text-emerald-700/80">Employees Processed</p></div>
                <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3"><p className="text-2xl font-bold text-blue-700">{bulkRecalcResult.records_updated}</p><p className="text-[10px] uppercase tracking-wider text-blue-700/80">Records Updated</p></div>
                <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3"><p className="text-2xl font-bold text-amber-700">{bulkRecalcResult.total_absorbed}</p><p className="text-[10px] uppercase tracking-wider text-amber-700/80">AL Days Absorbed</p></div>
                <div className="rounded-xl bg-red-50 dark:bg-red-900/20 p-3"><p className="text-2xl font-bold text-red-700">{bulkRecalcResult.total_lop_days}</p><p className="text-[10px] uppercase tracking-wider text-red-700/80">LOP Days</p></div>
              </div>
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-4">
                <span>Cycle: <strong>{bulkRecalcResult.cycle_start} → {bulkRecalcResult.cycle_end}</strong></span>
                <span>Audit logs written: <strong>{bulkRecalcResult.audit_logs_written}</strong></span>
                <span>Processed: <strong>{bulkRecalcResult.processed_at ? format(parseISO(bulkRecalcResult.processed_at), "dd MMM yyyy HH:mm") : "—"}</strong></span>
              </div>
              <div className="border rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-center">Used</TableHead>
                      <TableHead className="text-center">Remaining</TableHead>
                      <TableHead className="text-center">LOP</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkRecalcResult.details.map(d => (
                      <TableRow key={d.user_id}>
                        <TableCell className="font-medium">{d.name}</TableCell>
                        <TableCell className="text-center text-amber-700">{d.used}</TableCell>
                        <TableCell className="text-center text-emerald-700">{d.remaining}</TableCell>
                        <TableCell className={`text-center ${d.lop > 0 ? "text-red-600 font-medium" : "text-slate-400"}`}>{d.lop}</TableCell>
                        <TableCell className="text-xs text-slate-500">{!d.has_payroll ? "No payroll — skipped attendance walk" : `${(d.months || []).filter(m => m.absorbed > 0).length} month(s) absorbed`}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            {!bulkRecalcResult && !bulkRecalcLoading && <>
              <Button variant="outline" onClick={() => setBulkRecalcOpen(false)}>Cancel</Button>
              <Button onClick={runBulkRecalc} className="bg-indigo-600 hover:bg-indigo-700"><RefreshCw className="w-4 h-4 mr-2"/>Run Recalculation</Button>
            </>}
            {bulkRecalcResult && <Button onClick={() => setBulkRecalcOpen(false)}>Close</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──────── AL HISTORY DIALOG ──────── */}
      <Dialog open={alHistoryOpen} onOpenChange={setAlHistoryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>AL History — {alHistoryUser?.name} · {alOverview.year}</DialogTitle></DialogHeader>
          {alHistoryLoading ? (
            <div className="text-center py-10 text-slate-500">Loading…</div>
          ) : alHistory.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <Info className="w-10 h-10 mx-auto mb-3 opacity-40"/>
              <p>No absorption events recorded for {alOverview.year}.</p>
              <p className="text-xs mt-2">AL gets debited when a salary slip is generated for a month with absent days.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-center">Absent Days</TableHead>
                    <TableHead className="text-center">Leave Absorbed</TableHead>
                    <TableHead className="text-center">Chargeable (LOP)</TableHead>
                    <TableHead className="text-center">Balance Before</TableHead>
                    <TableHead className="text-center">Balance After</TableHead>
                    <TableHead>Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alHistory.map((h) => (
                    <TableRow key={h.month}>
                      <TableCell className="font-medium">{h.month_name} {alOverview.year}</TableCell>
                      <TableCell className="text-center">{h.absent_days ?? "—"}</TableCell>
                      <TableCell className="text-center text-purple-600 font-medium">{h.leave_absorbed}</TableCell>
                      <TableCell className={`text-center ${h.chargeable_absent_days > 0 ? "text-red-600 font-medium" : "text-slate-500"}`}>{h.chargeable_absent_days ?? "—"}</TableCell>
                      <TableCell className="text-center text-slate-600">{h.balance_before}</TableCell>
                      <TableCell className="text-center font-bold text-emerald-600">{h.balance_after}</TableCell>
                      <TableCell><div className="flex flex-wrap gap-1">{(h.types || []).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t.replace(/_/g, " ")}</Badge>)}</div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAlHistoryOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Presentational helpers ──────────────────────────────────────────────
function KpiCard({ label, value, suffix, subtle, icon, tint }) {
  const tints = {
    blue:    { bg: "bg-blue-50 dark:bg-blue-900/20",       num: "text-blue-700" },
    amber:   { bg: "bg-amber-50 dark:bg-amber-900/20",     num: "text-amber-700" },
    emerald: { bg: "bg-emerald-50 dark:bg-emerald-900/20", num: "text-emerald-700" },
    red:     { bg: "bg-red-50 dark:bg-red-900/20",         num: "text-red-700" },
  };
  const t = tints[tint] || tints.blue;
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl ${t.bg} flex items-center justify-center shrink-0`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 font-medium">{label}</p>
        <p className={`text-2xl font-bold leading-tight ${t.num}`}>{value} <span className="text-sm font-medium text-slate-500">{suffix}</span></p>
        <p className="text-[11px] text-slate-400 mt-0.5 truncate">{subtle}</p>
      </div>
    </div>
  );
}

function StatRow({ label, value, tone = "default", bold }) {
  const toneClass = {
    default: "text-slate-700 dark:text-slate-200",
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    red:     "text-red-600",
    purple:  "text-purple-700",
  }[tone] || "text-slate-700 dark:text-slate-200";
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <span className={`${toneClass} ${bold ? "font-bold" : "font-medium"} tabular-nums`}>{value}</span>
    </div>
  );
}

function LegendRow({ color, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}/>
        <span className="text-slate-600">{label}</span>
      </div>
      <span className="font-medium text-slate-800 tabular-nums">{value}</span>
    </div>
  );
}

// Pure-SVG donut: remaining (emerald) + consumed (amber) + LOP (red).
function LeaveDonut({ entitlement = 12, consumed = 0, lop = 0, remaining = 12 }) {
  const size = 180, stroke = 22, r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const total = Math.max(1, entitlement);
  const segments = [
    { color: "#10b981", value: Math.max(0, remaining) },
    { color: "#f59e0b", value: Math.max(0, consumed) },
    { color: "#ef4444", value: Math.max(0, lop) },
  ];
  let offset = 0;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke}/>
        {segments.map((seg, i) => {
          if (!seg.value) return null;
          const len = (seg.value / total) * C;
          const dasharray = `${len} ${C - len}`;
          const el = (
            <circle
              key={i}
              cx={size/2} cy={size/2} r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeDasharray={dasharray}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-emerald-700 tabular-nums">{Math.max(0, remaining)}</span>
        <span className="text-[11px] text-slate-500 uppercase tracking-wider">Days Remaining</span>
      </div>
    </div>
  );
}
